import React from 'react';
import CountrySelectInput from '../shared/countrySelectInput';
import { Select } from 'antd';
import { needsStateSelection } from './estimateFormHandlers';
import { getAlpha2Code } from '../../lib/helper/countryToAphlaCode';
import { CANADIAN_PROVINCES, BRAZILIAN_STATES } from '../../utils/states';

const EstimateFormDetails = ({ isGuest, formData, setFormData, errors, setErrors, handleInputChange, isReadOnly }) => {
    const getInputClassName = fieldName =>
        `w-full h-8 p-2 text-sm border rounded-md outline-none ${errors[fieldName] ? "border-red-500" : "active:border-cus-blue focus:border-cus-blue"}`;

    const getSelectClassName = fieldName => (errors[fieldName] ? "border-red-500" : "");

    const getAvailableStates = country => {
        const countryCode = getAlpha2Code(country);
        return countryCode === "CA" ? CANADIAN_PROVINCES.map(p => ({ value: p.state, label: p.state })) :
            countryCode === "BR" ? BRAZILIAN_STATES.map(s => ({ value: s.state, label: s.state })) : [];
    };

    return (
        <div className="flex flex-col space-y-4">
            {isGuest && (
                <div className="grid grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-normal">Name <span className="text-red-500">*</span></label>
                        <input
                            type="text"
                            className={getInputClassName("userName")}
                            name="userName"
                            value={formData.userName}
                            onChange={e => handleInputChange(e, setFormData, setErrors, needsStateSelection)}
                            readOnly={isReadOnly}
                        />
                        {errors.userName && <p className="text-red-500 text-xs mt-1">Name is required</p>}
                    </div>
                    <div className="flex flex-col gap-1">
                        <label className="text-sm font-normal">Email <span className="text-red-500">*</span></label>
                        <input
                            type="email"
                            className={getInputClassName("email")}
                            name="email"
                            value={formData.email}
                            onChange={e => handleInputChange(e, setFormData, setErrors, needsStateSelection)}
                            readOnly={isReadOnly}
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1">Email is required</p>}
                    </div>
                </div>
            )}
            <div className="grid grid-cols-2 gap-4">
                {["originCountry", "destinationCountry"].map(country => (
                    <div key={country} className="flex flex-col gap-1">
                        <label className="text-sm font-normal">
                            {country === "originCountry" ? "Origin Country" : "Destination Country"} <span className="text-red-500">*</span>
                        </label>
                        <CountrySelectInput
                            onChange={e => handleInputChange(e, setFormData, setErrors, needsStateSelection)}
                            value={formData[country] || ""}
                            name={country}
                            readOnly={isReadOnly}
                            className={`${getInputClassName(country)} !p-0`}
                            styles={{
                                control: provided => ({ ...provided, minHeight: "2rem", height: "2rem" }),
                                valueContainer: provided => ({ ...provided, height: "2rem", padding: "0 6px" }),
                                input: provided => ({ ...provided, margin: "0px" }),
                            }}
                        />
                        {errors[country] && <p className="text-red-500 text-xs mt-1">{country === "originCountry" ? "Origin Country" : "Destination Country"} is required</p>}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-2 gap-4">
                {["originCity", "destinationCity"].map(city => (
                    <div key={city} className="flex flex-col gap-1">
                        <label className="text-sm font-normal">
                            {city === "originCity" ? "Origin City" : "Destination City"} <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            className={getInputClassName(city)}
                            name={city}
                            value={formData[city] || ""}
                            onChange={e => handleInputChange(e, setFormData, setErrors, needsStateSelection)}
                            readOnly={isReadOnly}
                        />
                        {errors[city] && <p className="text-red-500 text-xs mt-1">{city === "originCity" ? "Origin City" : "Destination City"} is required</p>}
                        {needsStateSelection(formData[city === "originCity" ? "originCountry" : "destinationCountry"]) && (
                            <div className="mt-2">
                                <label className="text-sm font-normal">
                                    {city === "originCity" ? "Origin State" : "Destination State"} <span className="text-red-500">*</span>
                                </label>
                                <Select
                                    showSearch
                                    options={getAvailableStates(formData[city === "originCity" ? "originCountry" : "destinationCountry"])}
                                    onChange={value => handleInputChange({ target: { name: city === "originCity" ? "originState" : "destinationState", value } }, setFormData, setErrors, needsStateSelection)}
                                    value={formData[city === "originCity" ? "originState" : "destinationState"] || ""}
                                    style={{ width: "100%" }}
                                    className={getSelectClassName(city === "originCity" ? "originState" : "destinationState")}
                                    disabled={isReadOnly}
                                />
                                {errors[city === "originCity" ? "originState" : "destinationState"] && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {city === "originCity" ? "Origin State" : "Destination State"} is required
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-700 p-3 mt-4 mb-4 rounded-md shadow-sm flex items-center">
                <span className="text-2xl mr-3" aria-hidden="true">✈️</span>
                <div>
                    <p className="font-semibold">Transport Mode Information</p>
                    <p>Currently, only Air Shipments are supported.</p>
                </div>
            </div>
        </div>
    );
};

export default EstimateFormDetails;